import React, { useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import { Button, TitleWithDescription, InfoBox } from '@infoods/lifebase-ui';
import { useToggle } from 'react-use';
import { observer } from 'mobx-react';

import classes from './CoachSeatsWidget.module.scss';
import { angularizeWithProviders } from '../../../shared/providers';
import { ModeButtonsBlock } from '../../../shared/components/buttons';
import { CoachSeats, CoachSeatsFormValues } from '../../interfaces/coachSeats';
import { useMode } from '../../../shared/hooks/useMode';
import { entityGeneralSetupService } from '../../services/entityGeneralSetupService';
import { CoachSeatsInput } from './CoachSeatsInput/CoachSeatsInput';
import { AddSeatsModal } from '../AddSeatsModal/AddSeatsModal';
import { loggedInUserDataStore } from '../../../shared/stores/loggedInUserDataStore';
import { useUserRoleLabel } from '../../../shared/hooks/useUserRoleLabel';

type CoachSeatsWidgetProps = {
  entityId: number;
  mode: 'EDIT' | 'CREATE';
  onSuccessSubmit?: () => void;
};

const CoachSeatsWidget = observer(
  ({ entityId, mode, onSuccessSubmit = () => {} }: CoachSeatsWidgetProps) => {
    const isCreateMode = mode === 'CREATE';
    const isEditMode = mode === 'EDIT';

    const { isSuperAdmin, isInbodyAdmin } = loggedInUserDataStore;
    const { getForAgent } = useUserRoleLabel();

    const [coachSeats, setCoachSeats] = useState<CoachSeats>();
    const [addSeatsModalOpen, toggleAddSeatsModalOpen] = useToggle(false);

    const defaultViewMode = isCreateMode ? 'EDIT' : 'VIEW';
    const {
      mode: viewMode,
      setView,
      setEdit,
    } = useMode(isSuperAdmin ? defaultViewMode : 'VIEW');

    const { control, reset, handleSubmit } = useForm<CoachSeatsFormValues>();

    const getCoachSeats = async () => {
      const data = await entityGeneralSetupService.getCoachSeats(entityId);

      setCoachSeats(data);
      reset({ coachSeats: data.coachSeats });
    };

    useEffect(() => {
      // skip on create mode because of 404 error
      if (isEditMode) {
        getCoachSeats();
      }
    }, [entityId]);

    const handleCancel = () => {
      setView();
      reset();
    };

    const onSubmit = handleSubmit(async (data) => {
      const isCreated = !!coachSeats;

      const saved = isCreated
        ? await entityGeneralSetupService.updateCoachSeats(entityId, data)
        : await entityGeneralSetupService.createCoachSeats(entityId, data);

      // update form baseline so handleCancel/isDirty reflect the saved value
      reset({ coachSeats: saved.coachSeats });
      setCoachSeats((prev) => ({
        usedSeats: prev?.usedSeats ?? 0,
        coachSeats: saved.coachSeats,
      }));

      setView();
      onSuccessSubmit();
    });

    const ManageButtons = (
      <ModeButtonsBlock
        mode={viewMode}
        onEdit={setEdit}
        onCancel={handleCancel}
        onSubmit={onSubmit}
      />
    );

    const showAddSeatsButton = !isCreateMode && viewMode === 'VIEW' && !isInbodyAdmin;

    return (
      <>
        <InfoBox title="General Setup" adornment={isSuperAdmin && ManageButtons}>
          <div className={classes.container}>
            <TitleWithDescription
              title="Team Management"
              description={
                <>
                  Expand your team by adding extra {getForAgent({ case: 'lower' })} seats.
                  <br />
                  Additional seats are billed only through GHL
                </>
              }
              titleFont="body-14-bold"
              className={classes.descriptionBlock}
            />
            <div>
              <div className={classes.coachSeatsBlock}>
                <TitleWithDescription
                  title={`${getForAgent()} Seats`}
                  description={`The number of ${getForAgent({ case: 'lower', pluralize: true })} included in your plan`}
                  titleFont="body-14-bold"
                />
                <CoachSeatsInput
                  control={control}
                  usedSeats={coachSeats?.usedSeats}
                  mode={viewMode}
                />
              </div>
              {showAddSeatsButton && (
                <Button
                  className={classes.addSeatsButton}
                  variant="contained"
                  size="small"
                  onClick={toggleAddSeatsModalOpen}
                >
                  Add Seats
                </Button>
              )}
            </div>
          </div>
        </InfoBox>
        <AddSeatsModal
          open={addSeatsModalOpen}
          onClose={toggleAddSeatsModalOpen}
          entityId={entityId}
          coachSeats={coachSeats?.coachSeats || 0}
          usedSeats={coachSeats?.usedSeats || 0}
        />
      </>
    );
  },
);

angularizeWithProviders(CoachSeatsWidget, 'ngzCoachSeatsWidget', {
  entityId: '<',
  mode: '@',
});

export { CoachSeatsWidget };
